import { motion } from 'motion/react';
import { Phone, FileText, CreditCard, Rocket, Shield, DollarSign, Languages, Lock, ArrowRight, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from '../i18n';

interface HowItWorksPageProps {
  onNavigateHome: () => void;
}

export function HowItWorksPage({ onNavigateHome }: HowItWorksPageProps) {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const steps = [
    {
      number: 1,
      icon: Phone,
      titleKey: 'howItWorks.process.steps.booking.title',
      descriptionKey: 'howItWorks.process.steps.booking.description',
    },
    {
      number: 2,
      icon: FileText,
      titleKey: 'howItWorks.process.steps.recommendation.title',
      descriptionKey: 'howItWorks.process.steps.recommendation.description',
    },
    {
      number: 3,
      icon: CreditCard,
      titleKey: 'howItWorks.process.steps.payment.title',
      descriptionKey: 'howItWorks.process.steps.payment.description',
    },
    {
      number: 4,
      icon: Rocket,
      titleKey: 'howItWorks.process.steps.execution.title',
      descriptionKey: 'howItWorks.process.steps.execution.description',
    },
  ];

  const expectations = [
    {
      icon: Shield,
      titleKey: 'howItWorks.expectations.items.guidance.title',
      descriptionKey: 'howItWorks.expectations.items.guidance.description',
    },
    {
      icon: DollarSign,
      titleKey: 'howItWorks.expectations.items.pricing.title',
      descriptionKey: 'howItWorks.expectations.items.pricing.description',
    },
    {
      icon: Languages,
      titleKey: 'howItWorks.expectations.items.bilingual.title',
      descriptionKey: 'howItWorks.expectations.items.bilingual.description',
    },
    {
      icon: Lock,
      titleKey: 'howItWorks.expectations.items.confidential.title',
      descriptionKey: 'howItWorks.expectations.items.confidential.description',
    },
  ];

  const faqs = [
    {
      questionKey: 'howItWorks.faq.questions.freeCall.question',
      answerKey: 'howItWorks.faq.questions.freeCall.answer',
    },
    {
      questionKey: 'howItWorks.faq.questions.oneTime.question',
      answerKey: 'howItWorks.faq.questions.oneTime.answer',
    },
    {
      questionKey: 'howItWorks.faq.questions.legal.question',
      answerKey: 'howItWorks.faq.questions.legal.answer',
    },
    {
      questionKey: 'howItWorks.faq.questions.cost.question',
      answerKey: 'howItWorks.faq.questions.cost.answer',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Section 1: Hero Header */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/90">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-white mb-8 text-[40px] font-bold">
              {t('howItWorks.hero.title')}
            </h1>
            <p className="text-white/80 max-w-3xl mx-auto leading-relaxed text-lg text-[16px]">
              {t('howItWorks.hero.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 2: Our 4-Step Process */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[32px]">{t('howItWorks.process.title')}</h2>
            <p className="text-[#0A1A33]/60 max-w-2xl mx-auto">
              {t('howItWorks.process.subtitle')}
            </p>
          </motion.div>

          {/* Desktop Timeline */}
          <div className="hidden lg:block relative">
            {/* Connection Line */}
            <div className="absolute top-16 left-0 right-0 h-1 bg-[#C6A667]/20 mx-24" />
            
            <div className="grid grid-cols-4 gap-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="relative"
                  >
                    {/* Icon Circle */}
                    <div className="relative z-10 w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#C6A667] to-[#D4B578] flex items-center justify-center shadow-lg">
                      <Icon className="w-12 h-12 text-white" />
                      {/* Step Number Badge */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#0A1A33] text-white flex items-center justify-center text-sm">
                        {step.number}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-[#0A1A33] mb-3 text-center font-bold">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-[#0A1A33]/70 text-sm leading-relaxed text-center">
                      {t(step.descriptionKey)}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Mobile Stacked Cards */}
          <div className="lg:hidden space-y-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl shadow-lg border border-[#C6A667]/20"
                >
                  <div className="flex items-start gap-4">
                    <div className="relative flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-[#C6A667] to-[#D4B578] flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" />
                      <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#0A1A33] text-white flex items-center justify-center text-xs">
                        {step.number}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[#0A1A33] mb-2">
                        {t(step.titleKey)}
                      </h3>
                      <p className="text-[#0A1A33]/70 text-sm leading-relaxed">
                        {t(step.descriptionKey)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3: What You Can Expect */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[32px]">{t('howItWorks.expectations.title')}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {expectations.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-14 h-14 mb-6 rounded-full bg-[#C6A667]/10 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-3">
                    {t(item.titleKey)}
                  </h3>
                  <p className="text-[#0A1A33]/70 leading-relaxed">
                    {t(item.descriptionKey)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4: FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[32px] font-bold font-normal">{t('howItWorks.faq.title')}</h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-[#0A1A33]/10 rounded-2xl overflow-hidden hover:border-[#C6A667]/30 transition-colors"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="text-[rgb(0,0,0)] pr-4 text-[18px] font-bold font-normal">
                    {t(faq.questionKey)}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#C6A667] flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: openFaq === index ? 'auto' : 0,
                    opacity: openFaq === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-5 text-[#0A1A33]/70 leading-relaxed">
                    {t(faq.answerKey)}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A1A33] via-[#0D2347] to-[#0A1A33]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-white mb-8 font-bold text-[24px]">
              {t('howItWorks.finalCta.title')}
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="group px-10 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-[#0A1A33] rounded-full transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center gap-3"
            >
              <span>{t('howItWorks.finalCta.button')}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
